import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { apiClient } from '../lib/api-client';
import { Activity, FileText, Link as LinkIcon } from 'lucide-react';
import { TenantHeading, TenantLogo } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { Skeleton } from '../components/ui/Skeleton';
import { JiraDetailsDialog } from '../components/JiraDetailsDialog';
import { Button } from '../components/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Badge } from '../components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { jiraJqlFromTemplate, jiraListUrl, jiraOrJql, openInNewTab } from '../lib/jira-links';

export function ServiceHealth() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('service_health', 'read');
  const tenantMetaMap = useTenantMetaMap(!!user && canView);

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const tenantsToQuery = useMemo(() => {
    const list = (selectedTenants && selectedTenants.length > 0) ? selectedTenants : accessibleTenants;
    return (list || []).map((t) => String(t).toLowerCase());
  }, [accessibleTenants, selectedTenants]);

  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  
  const monthString = `${year}-${String(month).padStart(2, '0')}`;

  const [tenantData, setTenantData] = useState<Array<{
    tenantCode: string;
    tenantName: string;
    tenantColor?: string;
    data: any;
    configured?: {
      uptime?: boolean;
      alertmanager?: boolean;
      grafana?: boolean;
    };
    error?: string;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jiraDialogOpen, setJiraDialogOpen] = useState(false);
  const [selectedTenantForJira, setSelectedTenantForJira] = useState<{ code: string; name: string } | null>(null);
  const [alertsAlarmsDialogOpen, setAlertsAlarmsDialogOpen] = useState(false);
  const [selectedTenantForAlertsAlarms, setSelectedTenantForAlertsAlarms] = useState<{ tenantCode: string; name: string; alarms: any[]; alerts: any[]; defaultTab?: 'alarms' | 'alerts' } | null>(null);
  const dataFingerprintRef = useRef<string>('');
  const isInitialLoadRef = useRef(true);

  const openJiraList = useCallback((args: { jiraLinkConfig: any; projectKey: string; templateKey: string }) => {
    const cfg = args.jiraLinkConfig;
    const baseUrl = String(cfg?.baseUrl || '');
    const jql = jiraJqlFromTemplate({
      jiraLinkConfig: cfg,
      templateKey: args.templateKey,
      projectKey: args.projectKey,
      stripOrderBy: true,
    });
    openInNewTab(jql ? jiraListUrl(baseUrl, jql) : null);
  }, []);

  const openJiraListTotal = useCallback((args: { jiraLinkConfig: any; projectKey: string; templateKeys: string[] }) => {
    const cfg = args.jiraLinkConfig;
    const baseUrl = String(cfg?.baseUrl || '');
    const jqls = args.templateKeys.map((k) => jiraJqlFromTemplate({
      jiraLinkConfig: cfg,
      templateKey: k,
      projectKey: args.projectKey,
      stripOrderBy: true,
    }));
    const jql = jiraOrJql(jqls);
    openInNewTab(jql ? jiraListUrl(baseUrl, jql) : null);
  }, []);

  // Mark incident-store pages as active so backend monitoring caches use their active refresh cadence.
  useActivityHeartbeat('cache:service_health', !!user && canView);
  useActivityHeartbeat('cache:alerts', !!user && canView);

  const openAlertsAlarmsDialog = useCallback(async (args: { tenantCode: string; tenantName: string; defaultTab: 'alarms' | 'alerts'; source?: string; risk?: string }) => {
    try {
      const current = await apiClient.getMonitoringCurrent(args.tenantCode);
      const alarms = Array.isArray(current?.current?.alarms) ? current.current.alarms : [];
      const alerts = Array.isArray(current?.current?.alerts) ? current.current.alerts : [];
      const src = String(args.source || '').trim().toLowerCase();
      const risk = String(args.risk || '').trim().toLowerCase();

      const filter = (list: any[]) => {
        return (list || []).filter((i) => {
          if (src && String(i?.source || '').toLowerCase() !== src) return false;
          if (risk && String(i?.risk || '').toLowerCase() !== risk) return false;
          return true;
        });
      };

      setSelectedTenantForAlertsAlarms({
        tenantCode: args.tenantCode,
        name: args.tenantName,
        alarms: src || risk ? filter(alarms) : alarms,
        alerts: src || risk ? filter(alerts) : alerts,
        defaultTab: args.defaultTab,
      });
      setAlertsAlarmsDialogOpen(true);
    } catch (e) {
      console.error('Failed to fetch alarms/alerts:', e);
    }
  }, []);

  const renderIncident = (i: any, severity: 'alarm' | 'alert', idx?: number) => {
    const isAlarm = severity === 'alarm';
    const borderColor = isAlarm ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-orange-500 bg-orange-50 dark:bg-orange-900/10';
    const textColor = isAlarm ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300';
    
    // Generate unique key including environment to avoid duplicates
    const uniqueKey = idx !== undefined
      ? `${i.source}-${i.environment || 'noenv'}-${i.monitorId || i.serviceId}-${i.startedAt}-${idx}`
      : `${i.source}-${i.environment || 'noenv'}-${i.monitorId || i.serviceId}-${i.startedAt}`;
    
    return (
      <div key={uniqueKey} className={`rounded-lg border-l-4 ${borderColor} p-3`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className={`text-sm font-bold ${textColor} uppercase`}>{severity}</div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {i.environment && (
              <Badge variant="outline" className="text-xs">
                {String(i.environment)}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {String(i.source || '').charAt(0).toUpperCase() + String(i.source || '').slice(1)}
            </Badge>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{i.title}</div>
        {i.description && (
          <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 italic">
            {i.description}
          </div>
        )}
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          <span>{i.serviceName || i.serviceId}</span>
          <span className="mx-1">·</span>
          <span>since {new Date(i.startedAt).toLocaleString()}</span>
        </div>
      </div>
    );
  };

  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    if (!canView) return;
    // Only show loading skeleton on initial load, not on background refreshes
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    try {
      const tenantsResponse = await apiClient.getTenants();
      const tenantsArr = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const meta = new Map<string, { name?: string; color?: string }>(
        tenantsArr.map((t: any) => [String(t.code || '').toLowerCase(), { name: t.name, color: t.color }]),
      );

      const results = await Promise.all(
        (tenantsToQuery || []).map(async (tc) => {
          try {
            const [res, current] = await Promise.all([
              apiClient.getServiceHealthMonthly(tc, monthString),
              apiClient.getMonitoringCurrent(tc).catch(() => null), // Get configured status
            ]);
            return {
              tenantCode: tc,
              data: res,
              configured: current?.sources ? {
                uptime: (current.sources.uptime?.configured || false) && (current.sources.uptime?.configuredEnvironments?.length || 0) > 0,
                alertmanager: (current.sources.alertmanager?.configured || false) && (current.sources.alertmanager?.configuredEnvironments?.length || 0) > 0,
                grafana: current.sources.grafana?.enabled || false,
              } : undefined,
              error: undefined,
            };
          } catch (e: any) {
            return { tenantCode: tc, data: null, configured: undefined, error: e?.message || 'Failed to load service health' };
          }
        }),
      );
      
      // Create fingerprint of the data to detect actual changes (exclude fetchedAt timestamps)
      const fingerprint = JSON.stringify(results.map(r => ({
        tenantCode: r.tenantCode,
        jira: r.data?.jira ? {
          configured: r.data.jira.configured,
          counts: r.data.jira.counts,
          live: r.data.jira.live ? {
            configured: r.data.jira.live.configured,
            counts: r.data.jira.live.counts,
          } : undefined,
        } : undefined,
        availability: r.data?.availability ? {
          uptime: r.data.availability.uptime,
          alertmanager: r.data.availability.alertmanager,
          grafana: r.data.availability.grafana,
          last30Days: r.data.availability.last30Days,
        } : undefined,
        synthetic: r.data?.synthetic ? {
          configured: r.data.synthetic.configured,
          latest: r.data.synthetic.latest ? {
            status: r.data.synthetic.latest.status,
            failedCount: r.data.synthetic.latest.failedCount,
          } : undefined,
        } : undefined,
        configured: r.configured,
        error: r.error,
      })));
      
      // Only update if data actually changed
      if (fingerprint !== dataFingerprintRef.current) {
        dataFingerprintRef.current = fingerprint;
        setTenantData(
          results.map((r) => {
            const m = meta.get(r.tenantCode);
            return {
              tenantCode: r.tenantCode,
              tenantName: m?.name || r.tenantCode.toUpperCase(),
              tenantColor: m?.color,
              data: r.data,
              configured: r.configured,
              error: r.error,
            };
          }),
        );
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load service health');
    } finally {
      setLoading(false);
    }
  }, [canView, tenantsToQuery.join(','), monthString]);

  // Listen for cache events to reload when monitoring data changes
  useCacheEvents({
    enabled: !!user && canView,
    // Use semantic incident-store events instead of raw polling caches (uptime/alertmanager) to avoid noisy refresh.
    cacheNames: ['jira', 'synthetic', 'cache:alerts', 'cache:service_health'],
    onEvent: () => {
      // Refetch data when cache changes (background refresh, no loading state)
      if (canView && tenantsToQuery.length > 0) {
        void fetchData(true); // true = background refresh
      }
    },
  });

  useEffect(() => {
    isInitialLoadRef.current = true;
    void fetchData(false); // false = initial load, show loading skeleton
  }, [fetchData]);

  const visibleTenants = useMemo(() => {
    return tenantData.filter((t) => {
      const d = t.data || {};
      const jira = (d as any)?.jira;
      const synthetic = (d as any)?.synthetic;
      return (
        !!t.error ||
        !!jira?.configured ||
        !!synthetic?.configured ||
        !!(t.configured?.uptime || t.configured?.alertmanager || t.configured?.grafana)
      );
    });
  }, [tenantData]);

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Service Health</CardTitle>
        </CardHeader>
        <CardContent>You don&apos;t have permission to view Service Health.</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Activity}
        description="Current month health summary across selected tenants"
        count={visibleTenants.length}
        countLabel="tenants"
      />

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      {loading && (
        <div className="space-y-6">
          {[1, 2].map((i) => (
            <div key={i} className="space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton variant="circle" className="w-10 h-10" />
                <Skeleton className="h-6 w-32" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((j) => (
                  <Card key={j} className="border-l-4">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton variant="circle" className="h-8 w-8" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Section: Current */}
                      <div className="pt-2">
                        <Skeleton className="h-3 w-20" />
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="text-center space-y-2">
                            <Skeleton className="h-8 w-14 mx-auto" />
                            <Skeleton className="h-3 w-20 mx-auto" />
                          </div>
                          <div className="text-center space-y-2">
                            <Skeleton className="h-8 w-14 mx-auto" />
                            <Skeleton className="h-3 w-24 mx-auto" />
                          </div>
                        </div>
                      </div>

                      <Skeleton className="h-px w-full" />

                      {/* Section: By Priority / Sources */}
                      <div className="pt-1">
                        <Skeleton className="h-3 w-24" />
                        <div className="mt-3 grid grid-cols-4 gap-2">
                          {[1, 2, 3, 4].map((k) => (
                            <div key={k} className="text-center space-y-2">
                              <Skeleton className="h-3 w-6 mx-auto" />
                              <div className="flex items-baseline justify-center gap-1">
                                <Skeleton className="h-6 w-8" />
                                <Skeleton className="h-3 w-2" />
                                <Skeleton className="h-6 w-8" />
                              </div>
                              <Skeleton className="h-3 w-16 mx-auto" />
                            </div>
                          ))}
                        </div>
                      </div>

                      <Skeleton className="h-px w-full" />

                      {/* Section: Last 30 Days / key signals */}
                      <div className="pt-1 space-y-3">
                        <Skeleton className="h-3 w-24" />
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center space-y-2">
                            <Skeleton className="h-8 w-14 mx-auto" />
                            <Skeleton className="h-3 w-24 mx-auto" />
                          </div>
                          <div className="text-center space-y-2">
                            <Skeleton className="h-8 w-14 mx-auto" />
                            <Skeleton className="h-3 w-24 mx-auto" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-5/6" />
                          <Skeleton className="h-4 w-2/3" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-6">
          {visibleTenants.map((t) => {
            const d = t.data || {};
            const jira = d?.jira;
            const synthetic = d?.synthetic;
            const availability = d?.availability;
            const tenantMeta = resolveTenantMeta(tenantMetaMap, t.tenantCode, {
              name: t.tenantName,
              color: t.tenantColor,
            });

            const uptimeAlarms = availability?.uptime?.alarms ?? 0;
            const uptimeAlerts = availability?.uptime?.alerts ?? 0;
            const alertmanagerAlarms = availability?.alertmanager?.alarms ?? 0;
            const alertmanagerAlerts = availability?.alertmanager?.alerts ?? 0;
            const amByRisk = availability?.alertmanager?.byRisk || null;
            const grafanaAlarms = 0; // Placeholder for now
            const grafanaAlerts = 0; // Placeholder for now
            const uptime30dAlarms = availability?.last30Days?.uptime?.alarms ?? 0;
            const uptime30dAlerts = availability?.last30Days?.uptime?.alerts ?? 0;
            const alertmanager30dAlarms = availability?.last30Days?.alertmanager?.alarms ?? 0;
            const alertmanager30dAlerts = availability?.last30Days?.alertmanager?.alerts ?? 0;
            const am30dByRisk = availability?.last30Days?.alertmanager?.byRisk || null;
            const grafana30dAlarms = 0; // Placeholder for now
            const grafana30dAlerts = 0; // Placeholder for now
            const liveCounts = jira?.live?.counts || {};
            const p1Unresolved = liveCounts.p1Unresolved ?? 0;
            const p2OpenOrWip = liveCounts.p2OpenOrWip ?? 0;

            // Approximate Jira alarms/alerts from live snapshot (matches incident-store logic: P1 => alarm, P2 open & wip => alert)
            const jiraAlarms = Number(p1Unresolved ?? 0) || 0;
            const jiraAlerts = Number(p2OpenOrWip ?? 0) || 0;
            const jira30dAlarms = 0; // not currently provided by API
            const jira30dAlerts = 0; // not currently provided by API

            const totalAlarms = uptimeAlarms + alertmanagerAlarms + grafanaAlarms + jiraAlarms;
            const totalAlerts = uptimeAlerts + alertmanagerAlerts + grafanaAlerts + jiraAlerts;
            const total30dAlarms = uptime30dAlarms + alertmanager30dAlarms + grafana30dAlarms + jira30dAlarms;
            const total30dAlerts = uptime30dAlerts + alertmanager30dAlerts + grafana30dAlerts + jira30dAlerts;
            const shortestSlaRemainingHours = (liveCounts.shortestSlaRemainingHours === null || liveCounts.shortestSlaRemainingHours === undefined)
              ? null
              : Number(liveCounts.shortestSlaRemainingHours);
            const shortestSlaTicketCount = liveCounts.shortestSlaTicketCount ?? 0;
            const shortestSlaTicketKey = (liveCounts as any).shortestSlaTicketKey ?? null;
            const lastCustomerCommentTicketKey = (liveCounts as any).lastCustomerCommentTicketKey ?? null;
            const lastCustomerCommentedAt = (liveCounts as any).lastCustomerCommentedAt ?? null;
            const shortestSlaTicket = (liveCounts as any).shortestSlaTicket ?? null;
            const lastCustomerTouchTicket = (liveCounts as any).lastCustomerTouchTicket ?? null;
            const jiraLinkConfig = jira?.jiraLinkConfig || null;
            const jiraProjectKey = String(jira?.jiraProjectKey || '').trim();

            return (
              <div key={t.tenantCode} className="space-y-4">
                <TenantHeading
                  tenant={tenantMeta}
                  title={tenantMeta.name}
                  subtitle={tenantMeta.code}
                />

                {t.error && (
                  <Card>
                    <CardContent className="py-6 text-sm text-red-600 dark:text-red-400">
                      {t.error}
                    </CardContent>
                  </Card>
                )}

                {!t.error && (
                  <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
                    {/* Ticket card (Jira-backed) - only show if configured */}
                    {jira?.configured && (
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between min-w-0">
                            <CardTitle className="text-base truncate">Service Desk</CardTitle>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label="Ticket details"
                              onClick={() => {
                                setSelectedTenantForJira({ code: t.tenantCode, name: t.tenantName });
                                setJiraDialogOpen(true);
                              }}
                              title="Details"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2.5">
                          {(() => {
                            const backlogP1 = Number(jira.counts?.backlogP1 ?? liveCounts.p1Unresolved ?? 0) || 0;
                            const backlogP2 = Number(jira.counts?.backlogP2 ?? 0) || 0;
                            const backlogP3 = Number(jira.counts?.backlogP3 ?? liveCounts.p3Unresolved ?? 0) || 0;
                            const backlogP4 = Number(jira.counts?.backlogP4 ?? liveCounts.p4Unresolved ?? 0) || 0;
                            const openP2 = Number(liveCounts.p2OpenOrWip ?? 0) || 0;
                            const openP3 = Number(liveCounts.p3OpenOrWip ?? 0) || 0;
                            const openP4 = Number(liveCounts.p4OpenOrWip ?? 0) || 0;
                            const openTotal = openP2 + openP3 + openP4;
                            const unresolvedTotal = backlogP1 + backlogP2 + backlogP3 + backlogP4;

                            const created30d = Number(jira.counts?.createdLast30Days ?? jira.counts?.createdThisMonth ?? 0) || 0;
                            const resolved30d = Number(jira.counts?.resolvedLast30Days ?? jira.counts?.resolvedThisMonth ?? 0) || 0;

                            const isRed = shortestSlaRemainingHours !== null && shortestSlaRemainingHours < 2;
                            const isAmber = shortestSlaRemainingHours !== null && shortestSlaRemainingHours >= 2 && shortestSlaRemainingHours <= 4;
                            const slaColorClass = isRed
                              ? 'text-red-600 dark:text-red-400'
                              : isAmber
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-gray-900 dark:text-gray-100';

                            return (
                              <>
                                {/* Row 1: totals (dashboard-style) */}
                                <div className="pt-2">
                                  <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Current</div>
                                  <div className="mt-2 grid grid-cols-2 gap-2">
                                    <div className="text-center">
                                      <button
                                        type="button"
                                        onClick={() => openJiraListTotal({
                                          jiraLinkConfig,
                                          projectKey: jiraProjectKey,
                                          templateKeys: ['jiraP2OpenOrWip', 'jiraP3OpenOrWip', 'jiraP4OpenOrWip'],
                                        })}
                                        disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                        className={`text-2xl font-bold tabular-nums leading-none ${openTotal > 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                                        title="Open ticket list"
                                      >
                                        {openTotal}
                                      </button>
                                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Open / WIP</div>
                                    </div>
                                    <div className="text-center">
                                      <button
                                        type="button"
                                        onClick={() => openJiraList({ jiraLinkConfig, projectKey: jiraProjectKey, templateKey: 'backlogTotal' })}
                                        disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                        className={`text-2xl font-bold tabular-nums leading-none ${unresolvedTotal > 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                                        title="Open ticket list"
                                      >
                                        {unresolvedTotal}
                                      </button>
                                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Unresolved</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Row 2: by priority (dashboard-style) */}
                                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">By Priority</div>
                                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {([
                                      { key: 'p1', label: 'P1', open: 0, unres: backlogP1, text: 'text-red-600 dark:text-red-400' },
                                      { key: 'p2', label: 'P2', open: openP2, unres: backlogP2, text: 'text-orange-600 dark:text-orange-400' },
                                      { key: 'p3', label: 'P3', open: openP3, unres: backlogP3, text: 'text-yellow-600 dark:text-yellow-400' },
                                      { key: 'p4', label: 'P4', open: openP4, unres: backlogP4, text: 'text-blue-600 dark:text-blue-400' },
                                    ] as const).map((p) => {
                                      const hasAny = p.open > 0 || p.unres > 0;
                                      const openTemplateKey =
                                        p.key === 'p2' ? 'jiraP2OpenOrWip'
                                          : p.key === 'p3' ? 'jiraP3OpenOrWip'
                                            : p.key === 'p4' ? 'jiraP4OpenOrWip'
                                              : null;
                                      const unresTemplateKey =
                                        p.key === 'p1' ? 'backlogP1'
                                          : p.key === 'p2' ? 'backlogP2'
                                            : p.key === 'p3' ? 'backlogP3'
                                              : 'backlogP4';
                                      return (
                                        <div key={p.key} className="text-center">
                                          <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">{p.label}</div>
                                          <div className="mt-1 flex items-baseline justify-center gap-1.5 sm:gap-2">
                                            {openTemplateKey ? (
                                              <button
                                                type="button"
                                                onClick={() => openJiraList({ jiraLinkConfig, projectKey: jiraProjectKey, templateKey: openTemplateKey })}
                                                disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                                className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${hasAny ? p.text : 'text-gray-400'}`}
                                                title="Open ticket list"
                                              >
                                                {p.open}
                                              </button>
                                            ) : (
                                              <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${hasAny ? p.text : 'text-gray-400'}`}>{p.open}</span>
                                            )}
                                            <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                            <button
                                              type="button"
                                              onClick={() => openJiraList({ jiraLinkConfig, projectKey: jiraProjectKey, templateKey: unresTemplateKey })}
                                              disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                              className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${hasAny ? p.text : 'text-gray-400'}`}
                                              title="Open ticket list"
                                            >
                                              {p.unres}
                                            </button>
                                          </div>
                                          <div className="mt-1 h-[20px] flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-500 leading-none whitespace-nowrap tracking-tight">open & wip / unresolved</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Row 3: SLA + last touch + 30d activity */}
                                <div className="pt-2 border-t border-blue-500 dark:border-blue-500/80">
                                  <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Last 30 Days</div>
                                  {/* Row 3a: Created/Resolved in the same format as the left card summary */}
                                  <div className="mt-2 grid grid-cols-2 gap-2">
                                    <div className="text-center">
                                      <button
                                        type="button"
                                        onClick={() => openJiraList({
                                          jiraLinkConfig,
                                          projectKey: jiraProjectKey,
                                          templateKey: jira?.counts?.createdLast30Days !== undefined ? 'createdLast30Days' : 'createdThisMonth',
                                        })}
                                        disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                        className={`text-2xl font-bold tabular-nums leading-none ${created30d > 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                                        title="Open ticket list"
                                      >
                                        {created30d}
                                      </button>
                                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Created (30d)</div>
                                    </div>
                                    <div className="text-center">
                                      <button
                                        type="button"
                                        onClick={() => openJiraList({
                                          jiraLinkConfig,
                                          projectKey: jiraProjectKey,
                                          templateKey: jira?.counts?.resolvedLast30Days !== undefined ? 'resolvedLast30Days' : 'resolvedThisMonth',
                                        })}
                                        disabled={!jiraLinkConfig?.baseUrl || !jiraProjectKey}
                                        className={`text-2xl font-bold tabular-nums leading-none ${resolved30d > 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                                        title="Open ticket list"
                                      >
                                        {resolved30d}
                                      </button>
                                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Resolved (30d)</div>
                                    </div>
                                  </div>

                                  {/* Row 3b: Ticket details (same data, cleaner layout) */}
                                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                                   <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Key Signals</div>

                                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      <div className="text-center">
                                        <button
                                          type="button"
                                          onClick={() => openInNewTab(shortestSlaTicket?.url || null)}
                                          disabled={!shortestSlaTicket?.url}
                                          className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${(shortestSlaTicket?.key || shortestSlaTicketKey) ? slaColorClass : 'text-gray-400'}`}
                                          style={isRed ? { animation: 'pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
                                          title="Open ticket"
                                        >
                                          {(() => {
                                            const key = shortestSlaTicket?.key || shortestSlaTicketKey || '—';
                                            const prio = shortestSlaTicket?.priority || '—';
                                            return `${key} · ${prio}`;
                                          })()}
                                        </button>
                                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 whitespace-normal break-words leading-snug">
                                          {(() => {
                                            const status = String(shortestSlaTicket?.status || '').trim() || '—';
                                            const h = (shortestSlaTicket?.timeToResolutionRemainingHours ?? shortestSlaRemainingHours);
                                            const sla = (h === null || h === undefined) ? 'SLA: —' : `SLA: ${Number(h).toFixed(1)}h`;
                                            const touched = shortestSlaTicket?.touchedAt ? ` · ${new Date(shortestSlaTicket.touchedAt).toLocaleString()}` : '';
                                            const count = shortestSlaTicketCount ? ` · ${shortestSlaTicketCount}` : '';
                                            return `${status} · ${sla}${touched}${count}`;
                                          })()}
                                        </div>
                                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words leading-snug">
                                          {String(shortestSlaTicket?.summary || '').trim() || '\u00A0'}
                                        </div>
                                        <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Shortest SLA remaining</div>
                                      </div>

                                      <div className="text-center">
                                        <button
                                          type="button"
                                          onClick={() => openInNewTab(lastCustomerTouchTicket?.url || null)}
                                          disabled={!lastCustomerTouchTicket?.url}
                                          className={`text-lg sm:text-xl font-bold leading-none ${(lastCustomerTouchTicket?.key || lastCustomerCommentTicketKey) ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                                          title="Open ticket"
                                        >
                                          {(() => {
                                            const key = lastCustomerTouchTicket?.key || lastCustomerCommentTicketKey || '—';
                                            const prio = lastCustomerTouchTicket?.priority || '—';
                                            return `${key} · ${prio}`;
                                          })()}
                                        </button>
                                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 whitespace-normal break-words leading-snug">
                                          {(() => {
                                            const status = String(lastCustomerTouchTicket?.status || '').trim() || '—';
                                            const h = lastCustomerTouchTicket?.timeToResolutionRemainingHours;
                                            const sla = (h === null || h === undefined) ? 'SLA: —' : `SLA: ${Number(h).toFixed(1)}h`;
                                            const touchedAt = lastCustomerTouchTicket?.touchedAt || lastCustomerCommentedAt;
                                            const touched = touchedAt ? ` · ${new Date(touchedAt).toLocaleString()}` : '';
                                            return `${status} · ${sla}${touched}`;
                                          })()}
                                        </div>
                                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words leading-snug">
                                          {String(lastCustomerTouchTicket?.summary || '').trim() || '\u00A0'}
                                        </div>
                                        <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Last customer touch</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    )}

                    {/* Availability Card - RED theme - only show if at least one source is configured */}
                    {(t.configured?.uptime || t.configured?.alertmanager || t.configured?.grafana) && (
                      <Card style={totalAlarms > 0 ? { animation: 'pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between min-w-0">
                          <CardTitle className="text-base truncate">Alerts and Alarms</CardTitle>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="View alarms and alerts"
                            onClick={async () => {
                              try {
                                await openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alarms' });
                              } catch (e) {
                                console.error('Failed to fetch alarms/alerts:', e);
                              }
                            }}
                            title="View Details"
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2.5">
                        {/* Current (dashboard-style) */}
                        <div className="pt-2">
                          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Current</div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div className="text-center">
                              <button
                                type="button"
                                onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alarms' })}
                                disabled={totalAlarms === 0}
                                className={`text-2xl font-bold tabular-nums leading-none ${totalAlarms > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                title="Open list"
                              >
                                {totalAlarms}
                              </button>
                              <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alarms</div>
                            </div>
                            <div className="text-center">
                              <button
                                type="button"
                                onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts' })}
                                disabled={totalAlerts === 0}
                                className={`text-2xl font-bold tabular-nums leading-none ${totalAlerts > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                title="Open list"
                              >
                                {totalAlerts}
                              </button>
                              <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alerts</div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">By Source</div>
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {([
                              { key: 'uptime', label: 'uptime', alarms: uptimeAlarms, alerts: uptimeAlerts },
                              { key: 'jira', label: 'ticket', alarms: jiraAlarms, alerts: jiraAlerts },
                              { key: 'grafana', label: 'grafana', alarms: grafanaAlarms, alerts: grafanaAlerts },
                            ] as const).map((src) => (
                              <div key={src.key} className="text-center">
                                <div className="mt-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase leading-none">{src.label}</div>
                                <div className="mt-1 flex items-baseline justify-center gap-1.5 sm:gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alarms', source: src.key })}
                                    disabled={src.alarms === 0}
                                    className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alarms > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                    title="Open list"
                                  >
                                    {src.alarms}
                                  </button>
                                  <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                  <button
                                    type="button"
                                    onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts', source: src.key })}
                                    disabled={src.alerts === 0}
                                    className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alerts > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                    title="Open list"
                                  >
                                    {src.alerts}
                                  </button>
                                </div>
                                <div className="mt-1 h-[20px] flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-500 leading-none whitespace-nowrap">alarms / alerts</div>
                              </div>
                            ))}

                            {/* Alertmanager (4 numbers) */}
                            <div className="text-center">
                              <div className="mt-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase leading-none">alertmanager</div>
                              <div className="mt-1 flex items-baseline justify-center gap-1.5 sm:gap-2 flex-nowrap">
                                {(() => {
                                  const c = Number(amByRisk?.critical ?? 0) || 0;
                                  const i = Number(amByRisk?.important ?? 0) || 0;
                                  const m = Number(amByRisk?.moderate ?? 0) || 0;
                                  const l = Number(amByRisk?.low ?? 0) || 0;
                                  return (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts', source: 'alertmanager', risk: 'critical' })}
                                        disabled={c === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${c > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                        title="Open list"
                                      >
                                        {c}
                                      </button>
                                      <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                      <button
                                        type="button"
                                        onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts', source: 'alertmanager', risk: 'important' })}
                                        disabled={i === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${i > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                        title="Open list"
                                      >
                                        {i}
                                      </button>
                                      <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                      <button
                                        type="button"
                                        onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts', source: 'alertmanager', risk: 'moderate' })}
                                        disabled={m === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${m > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400'}`}
                                        title="Open list"
                                      >
                                        {m}
                                      </button>
                                      <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                      <button
                                        type="button"
                                        onClick={() => void openAlertsAlarmsDialog({ tenantCode: t.tenantCode, tenantName: t.tenantName, defaultTab: 'alerts', source: 'alertmanager', risk: 'low' })}
                                        disabled={l === 0}
                                        className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${l > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
                                        title="Open list"
                                      >
                                        {l}
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="mt-1 h-[20px] flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-500 leading-none whitespace-nowrap">C / I / M / L</div>
                            </div>
                          </div>
                        </div>

                        {/* Last 30 Days (dashboard-style) */}
                        <div className="pt-2 border-t border-blue-500 dark:border-blue-500/80">
                          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Last 30 Days</div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div className="text-center">
                              <div className={`text-2xl font-bold tabular-nums leading-none ${total30dAlarms > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                                {total30dAlarms}
                              </div>
                              <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alarms</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-2xl font-bold tabular-nums leading-none ${total30dAlerts > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}>
                                {total30dAlerts}
                              </div>
                              <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alerts</div>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                            <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">By Source</div>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {([
                                { key: 'uptime', label: 'uptime', alarms: uptime30dAlarms, alerts: uptime30dAlerts },
                                { key: 'jira', label: 'ticket', alarms: jira30dAlarms, alerts: jira30dAlerts },
                                { key: 'grafana', label: 'grafana', alarms: grafana30dAlarms, alerts: grafana30dAlerts },
                              ] as const).map((src) => (
                                <div key={`30d-${src.key}`} className="text-center">
                                  <div className="mt-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase leading-none">{src.label}</div>
                                  <div className="mt-1 flex items-baseline justify-center gap-1.5 sm:gap-2">
                                    <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alarms > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{src.alarms}</span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                    <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${src.alerts > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}>{src.alerts}</span>
                                  </div>
                                  <div className="mt-1 h-[20px] flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-500 leading-none whitespace-nowrap">alarms / alerts</div>
                                </div>
                              ))}

                              {/* Alertmanager (4 numbers) */}
                              <div className="text-center">
                                <div className="mt-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase leading-none">alertmanager</div>
                                <div className="mt-1 flex items-baseline justify-center gap-1.5 sm:gap-2 flex-nowrap">
                                  {(() => {
                                    const c = Number(am30dByRisk?.critical ?? 0) || 0;
                                    const i = Number(am30dByRisk?.important ?? 0) || 0;
                                    const m = Number(am30dByRisk?.moderate ?? 0) || 0;
                                    const l = Number(am30dByRisk?.low ?? 0) || 0;
                                    return (
                                      <>
                                        <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${c > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{c}</span>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                        <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${i > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}>{i}</span>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                        <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${m > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400'}`}>{m}</span>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                                        <span className={`text-lg sm:text-xl font-bold tabular-nums leading-none ${l > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>{l}</span>
                                      </>
                                    );
                                  })()}
                                </div>
                                <div className="mt-1 h-[20px] flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-500 leading-none whitespace-nowrap">C / I / M / L</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                  </Card>
                    )}

                    {/* Synthetic Card - GREEN theme - only show if configured */}
                    {synthetic?.configured && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base truncate">Synthetic Tests</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {synthetic?.latest ? (
                            <>
                              <div className={`text-center p-3 rounded-lg ${synthetic.latest.status === 'passed' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                                <div className={`text-2xl font-bold ${synthetic.latest.status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                  {synthetic.latest.status}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Latest run</div>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Suite</span>
                                <span className="text-xs font-mono text-gray-900 dark:text-gray-100">{synthetic.latest.suiteId}</span>
                              </div>
                              <div className="pt-2 border-t border-gray-200 dark:border-gray-700 text-xs space-y-1">
                                <div><span className="text-gray-600 dark:text-gray-400">Started:</span> {new Date(synthetic.latest.startedAt).toLocaleString()}</div>
                                {synthetic.latest.finishedAt && (
                                  <div><span className="text-gray-600 dark:text-gray-400">Finished:</span> {new Date(synthetic.latest.finishedAt).toLocaleString()}</div>
                                )}
                              </div>
                              {synthetic.latest.reportUrl && (
                                <div className="pt-2">
                                  <a className="text-sm text-blue-600 dark:text-blue-400 underline hover:no-underline" href={synthetic.latest.reportUrl} target="_blank" rel="noreferrer">
                                    View detailed report →
                                  </a>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm text-gray-600 dark:text-gray-400 py-4">No runs yet</div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedTenantForJira && (
        <JiraDetailsDialog
          open={jiraDialogOpen}
          onClose={() => {
            setJiraDialogOpen(false);
            setSelectedTenantForJira(null);
          }}
          tenantCode={selectedTenantForJira.code}
          tenantName={selectedTenantForJira.name}
          currentMonth={monthString}
        />
      )}

      {/* Alerts and Alarms Dialog with Tabs */}
      {selectedTenantForAlertsAlarms && (
        <Dialog open={alertsAlarmsDialogOpen} onOpenChange={() => setAlertsAlarmsDialogOpen(false)}>
          <DialogContent maxWidth="3xl" className="flex flex-col max-h-[90vh]">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-3 min-w-0">
                <TenantLogo
                  tenant={resolveTenantMeta(tenantMetaMap, selectedTenantForAlertsAlarms.tenantCode, {
                    name: selectedTenantForAlertsAlarms.name,
                  })}
                  size="md"
                  rounded="lg"
                  className="border border-gray-200/80 dark:border-gray-700/80 shrink-0"
                />
                <span className="truncate">Current Alerts & Alarms — {selectedTenantForAlertsAlarms.name}</span>
              </DialogTitle>
            </DialogHeader>
            <DialogBody className="flex-1 overflow-y-auto min-h-0">
              <Tabs
                key={`${selectedTenantForAlertsAlarms.name}-${selectedTenantForAlertsAlarms.defaultTab || 'alarms'}`}
                defaultValue={selectedTenantForAlertsAlarms.defaultTab || 'alarms'}
                className="w-full h-full flex flex-col"
              >
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="alarms" className="flex items-center justify-center gap-1.5">
                    <span>Alarms</span>
                    {selectedTenantForAlertsAlarms.alarms.length > 0 && (
                      <Badge variant="destructive" className="h-5 min-w-[20px] px-1.5 text-xs">
                        {selectedTenantForAlertsAlarms.alarms.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="alerts" className="flex items-center justify-center gap-1.5">
                    <span>Alerts</span>
                    {selectedTenantForAlertsAlarms.alerts.length > 0 && (
                      <Badge className="h-5 min-w-[20px] px-1.5 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                        {selectedTenantForAlertsAlarms.alerts.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="alarms" className="flex-1 mt-0 overflow-y-auto">
                  <div className="space-y-2">
                    {selectedTenantForAlertsAlarms.alarms.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No current alarms
                      </div>
                    ) : (
                      selectedTenantForAlertsAlarms.alarms.map((i, idx) => renderIncident(i, 'alarm', idx))
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="alerts" className="flex-1 mt-0 overflow-y-auto">
                  <div className="space-y-2">
                    {selectedTenantForAlertsAlarms.alerts.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No current alerts
                      </div>
                    ) : (
                      selectedTenantForAlertsAlarms.alerts.map((i, idx) => renderIncident(i, 'alert', idx))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogBody>
            <DialogFooter className="flex-shrink-0">
              <Button onClick={() => setAlertsAlarmsDialogOpen(false)} className="w-full sm:w-auto">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <style>{`
        @keyframes pulse-red {
          0%, 100% {
            background-color: transparent;
          }
          50% {
            background-color: rgba(239, 68, 68, 0.1);
          }
        }
        @media (prefers-color-scheme: dark) {
          @keyframes pulse-red {
            0%, 100% {
              background-color: transparent;
            }
            50% {
              background-color: rgba(239, 68, 68, 0.2);
            }
          }
        }
      `}</style>
    </div>
  );
}
